import React, {useEffect, useMemo, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import {Alert, Button, Col, Row} from "reactstrap";
import moment from "moment/moment";
import { InputText } from "primereact/inputtext";
import Select from "react-select";

import {
  yearFormat,
  corpNameFormat,
  inspTypeColumn,
  showQceFormatter,
  elevatorFormat,
  superFormat,
  superPhoneFormat,
  createActionFormatter,
  createButtonHeader,
  yesNoFormatter,
  createEditButtonFormatter,
} from "./Formatters";
import {
  PROPOSAL_STATUSES,
  PROPOSAL_VIEW,
} from "../../../services/appConstants";
import * as newProposalActions from "../../../redux/actions/newProposalActions";
import DynamicCell from "./DynamicCellEdit";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ElevatorCompanyCell from "./ElevatorCompanyCellEdit";

const BuildingsTable = ({ view, onRowTouched }) => {
  const [expandedKeys, setExpandedKeys] = useState({});
  const [selectionKeys, setSelectionKeys] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [editEnabled, setEditEnabled] = useState(false);
  
  const [editingRowKey, setEditingRowKey] = useState(null);
  const editingRowDataRef = useRef(null); 

  let dispatch = useDispatch();

  const {
    expiringLines,
    lines,
    underContractLines,
    proposal,
    elevatorCompanies,
  } = useSelector((state) => state.newProposalReducer);

  useEffect(() => {
    dispatch(newProposalActions.setElevatorCompanies());
  }, []);

  useEffect(() => {
    setEditEnabled(
      [
        PROPOSAL_STATUSES.VIEWED.value,
        PROPOSAL_STATUSES.MODIFIED.value,
      ].includes(proposal.statusId)
    );
  }, [proposal.statusId]);

  const approveLine = (rowToUpdate) => {
    rowToUpdate.statusId = PROPOSAL_STATUSES.SENT.value;
    updateDevicesStatus(rowToUpdate.devices, PROPOSAL_STATUSES.SENT.value);

    dispatch(newProposalActions.updateBuilding(rowToUpdate));
    onRowTouched && onRowTouched(rowToUpdate);
  };

  const updateDevicesStatus = (devices, status) => {
    if (Array.isArray(devices)) {
      devices.forEach(device => {
        device.statusId = status;
        onRowTouched && onRowTouched(device);
      });
    }
  }
  const declineLine = (rowToUpdate) => {
    rowToUpdate.statusId = PROPOSAL_STATUSES.VIEWED.value;
    updateDevicesStatus(rowToUpdate.devices, PROPOSAL_STATUSES.VIEWED.value); 
    dispatch(newProposalActions.updateBuilding(rowToUpdate));
    onRowTouched && onRowTouched(rowToUpdate);
  };

  const updateAll = (status) => {
    const currentYear = moment(new Date()).year();
    const allLines = lines.map((item) => {
      if (item.agreementThroughYear <= currentYear) {
        updateDevicesStatus(item.devices, status);
        
        return { ...item, statusId: status };
      }
      
      return { ...item, statusId: PROPOSAL_STATUSES.SENT.value };
    });

    dispatch(newProposalActions.updateNewAllLines(allLines));
    allLines.forEach((l) => onRowTouched && onRowTouched(l));
  };

  const editDevice = (rowToUpdate) => {
    dispatch(newProposalActions.updateDeviceElevatorCompany(rowToUpdate));
    onRowTouched && onRowTouched(rowToUpdate);
  }

  const editBuilding = (rowToUpdate) => {
    dispatch(newProposalActions.updateBuilding(rowToUpdate));
    
    // if superName/superPhone/elevatorCompany changed, update all devices too and add them to the modified list
    if (rowToUpdate.devices && Array.isArray(rowToUpdate.devices)) {
      rowToUpdate.devices.forEach(device => {
        device.auditSuperName = rowToUpdate.auditSuperName;
        device.auditSuperPhone = rowToUpdate.auditSuperPhone;
        device.auditElevatorCompany = rowToUpdate.auditElevatorCompany;
        device.elevatorCompanyId = rowToUpdate.elevatorCompanyId;
        
        onRowTouched && onRowTouched(device);
      });
    }
    onRowTouched && onRowTouched(rowToUpdate);
  }

  const toTreeNodes = (rows) =>
    rows?.map((row, index) => ({
      key: String(index),
      data: { ...row },
      children: row.devices?.map((device, childIndex) => ({
        key: `${index}-${childIndex}`,
        data: device,
      })),
    }));

  const actionFormatter = createActionFormatter({
    view,
    canEdit: proposal?.canEdit,
    editBuilding,
    approveLine,
    declineLine,
  });
  const buttonHeader = createButtonHeader({
    view,
    canEdit: proposal?.canEdit,
    updateAll,
  });
  const editButtonFormatter = createEditButtonFormatter({
    canEdit: proposal?.canEdit,
    editDevice,
    view,
  });

  useMemo(() => {
    let formattedNodes = [];
    switch (view) {
      case PROPOSAL_VIEW.CURRENT:
        formattedNodes = underContractLines
          ? toTreeNodes(underContractLines)
          : [];
        break;
      case PROPOSAL_VIEW.EXPIRING:
        formattedNodes = expiringLines ? toTreeNodes(expiringLines) : [];
        break;
      case PROPOSAL_VIEW.ALL:
        formattedNodes = lines ? toTreeNodes(lines) : [];
        break;
      default:
        formattedNodes = [];
        break;
    }

    setNodes(formattedNodes);
  }, [expiringLines, lines, underContractLines, view]);

  const updateNodeData = (nodeKey, updates) => {
    setNodes((prevNodes) => {
      const newNodes = [...prevNodes]; // shallow clone top level
      const editedNode = findNodeByKey(newNodes, nodeKey);

      if (editedNode) {
        editedNode.data = { ...editedNode.data, ...updates };
      }

      return newNodes;
    });
  };

  const findNodeByKey = (nodes, key) => {
    let current = { children: nodes };

    for (const index of key.split("-").map(Number)) {
      if (!current.children || !current.children[index]) return null;
      current = current.children[index];
    }

    return current;
  };

  const elevatorCompanyEditor = (options) => (
    <Select
      value={{
        value: options.rowData.elevatorCompanyId,
        label: options.rowData.auditElevatorCompany,
      }}
      onChange={(e) => {
        updateNodeData(options.node.key, {
          elevatorCompanyId: e.value,
          auditElevatorCompany: e.label,
        });
      }}
      options={elevatorCompanies}
      onKeyDown={(e) => e.stopPropagation()}
    />
  );

  const validatePhone = (phone) => {
      const usPhoneRegex =
        /^(\+1\s?)?(\([2-9]\d{2}\)|[2-9]\d{2})[\s.-]?\d{3}[\s.-]?\d{4}$/;
      return usPhoneRegex.test(String(phone).trim());
    };
  const InputEditor = ({ options, onCommit, validatePhone }) => {
    const [value, setValue] = useState(options.rowData[options.field] ?? "");
    const [isValid, setIsValid] = useState(true);

    const handleCommit = () => {
        let valid = true;
        if(validatePhone){
            valid = validatePhone(value);
            setIsValid(valid);
        }else{
            setIsValid(true);
        }
      
      if (valid) {
        onCommit(options.node.key, { [options.field]: value });
      }
    };

    return (
      <div className="p-inputgroup">
        <InputText
          type="text"
          style={{
            width: "7rem",
            border: !isValid ? "1px solid red" : "",
          }}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setIsValid(true); // Reset validation on change
          }}
          onBlur={handleCommit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleCommit();
            }
            e.stopPropagation();
          }}
        />
        {!isValid && (
          <small
            className="p-error"
            style={{ color: "red", fontSize: "0.75rem" }}
          >
            Invalid phone format
          </small>
        )}
      </div>
    );
  };


  // Function to update editingRowData while editing
  const onEditInputChange = (field, value) => {
    if (editingRowDataRef.current) {
      editingRowDataRef.current = {
        ...editingRowDataRef.current,
        [field]: value,
      };
    }
  };

  const onEditDropDownChange = (values) => {
    editingRowDataRef.current = {
      ...editingRowDataRef.current,
    ...values
    };
  }


  const onEditRow = (key) => {
    const node = findNodeByKey(nodes, key); 
    if (node) {
      setEditingRowKey(key);
     editingRowDataRef.current = node.data 
    }
  };

  const onSaveRow = (key) => {
    if (editingRowDataRef.current) {
      
      if(!!editingRowDataRef.current.devices) {
        editBuilding(editingRowDataRef.current);
      }else{
        editDevice(editingRowDataRef.current);
      }
      
      // Reset local editing state
      setEditingRowKey(null);
      editingRowDataRef.current = null;
    }
  };

  const onCancelRow = () => {
    setEditingRowKey(null); 
    editingRowDataRef.current = null; 
  };

  const actionTemplate = (node) => {
    if (editingRowKey === node.key) {
      return (
          <>
            <Button
                size="sm"
                outline={true}
                className="fs--1 mr-1"
                color={ "secondary"}
                onClick={() => onSaveRow(node.key)}
            >
              <FontAwesomeIcon icon="check" className="fs--4"/>
            </Button>
            <Button
                size="sm"
                outline={true}
                className="fs--1 mr-1"
                color={ "secondary"}
                onClick={onCancelRow}
            >
              <FontAwesomeIcon icon="times-circle" className="fs--4"/>
            </Button>
          </>
      );
    }
    return (
        <Button
            size="sm"
            outline
            disabled={!proposal?.canEdit}
            className="fs--2 mr-2"
            color="info"
            onClick={() => onEditRow(node.key)}>
        <FontAwesomeIcon icon="edit" className="fs--4"/>
        </Button>
    );
  };
  
  return (
    <>
      <Row className="align-items-center">
        <Col className="col-6 text-left  py-2">
          <h5>Buildings List</h5>
        </Col>
        <Col className="col-6 text-right py-2"></Col>
      </Row>
      {nodes?.length ? (
        <div className="card">
          <TreeTable
            value={nodes}
            selectionMode="single"
            selectionKeys={selectionKeys}
            onSelectionChange={(e) => setSelectionKeys(e.value)}
            expandedKeys={expandedKeys}
            onToggle={(e) => setExpandedKeys(e.value)}
            className="fs--1"
          >
            <Column expander style={{ width: "4rem" }} />
            <Column
              field="agreementThroughYear"
              header="Contract Duration"
              sortable
              body={(rowData) =>
                rowData.children ? yearFormat(rowData.data, proposal.year) : null
              }
            />
            <Column
              field="buildingAddress"
              header="Building Address"
              sortable
              body={(rowData) =>
                rowData.data ? rowData.data.buildingAddress : null
              }
            />
            <Column
              field="auditCorpName"
              header="Corp Building Name"
              sortable
              body={(rowData) =>
                rowData.data ? corpNameFormat(rowData.data) : null
              }
            />
            <Column
              field="devices"
              header="Devices"
              sortable
              style={{ width: "6rem" }}
              body={(rowData) =>
                rowData.children
                  ? rowData.data.devices?.length
                  : rowData.data?.device
              }
              sortFunction={(e) => {
                const nodes = e.data.filter((node) => node.children);
                nodes.sort(
                  (a, b) =>
                    e.order *
                    ((a.data.devices?.length ?? 0) -
                      (b.data.devices?.length ?? 0))
                );

                return nodes;
              }}
            />
            <Column
              field="isPeriodic"
              header="Inspection Types"
              sortable
              body={(rowData) =>
                rowData.data ? inspTypeColumn(rowData.data) : null
              }
            />
            <Column
              field="auditAgreedToExpeditingFee"
              header="Expedite Filing Fee"
              sortable
              style={{ width: "4rem" }}
              body={(rowData) =>
                rowData.children
                  ? yesNoFormatter(rowData.data?.auditAgreedToExpeditingFee, rowData.data?.agreedToExpeditingFee, rowData.data?.isUnderContract)
                  : null
              }
            />
            <Column
              field="auditTrackAoc"
              header="AOC Tracking"
              sortable
              style={{ width: "4rem" }}
              body={(rowData) =>
                rowData.data ? yesNoFormatter(rowData.data?.auditTrackAoc, rowData.data?.trackAoc, rowData.data?.isUnderContract) : null
              }
            />
            <Column
              field="auditQce"
              header="QCEs"
              sortable
              style={{ width: "4rem" }}
              body={(rowData) =>
                rowData.data ? showQceFormatter(rowData.data) : null
              }
            />
            <Column
              field="elevatorCompanyId"
              header="Elevator Company"
              sortable
              body={(node) => <ElevatorCompanyCell
                  node={node} field={"elevatorCompanyId"} 
                  editingRowKey={editingRowKey}
                  dropDownOptions={elevatorCompanies}
                  onEditInputChange={onEditDropDownChange}/>}
            />
            <Column
              field="auditSuperName"
              header="Super Name"
              sortable
              body={(node) => <DynamicCell
                  node={node} field={"auditSuperName"}
                  editingRowKey={editingRowKey}
                  onEditInputChange={onEditInputChange}/>}
            />
            <Column
              field="auditSuperPhone"
              header="Super Phone"
              sortable
              body={(node) => <DynamicCell
                  node={node} field={"auditSuperPhone"}
                  isPhone={true}
                  editingRowKey={editingRowKey} 
                  onEditInputChange={onEditInputChange}/>}
              
            />
            <Column
                header="Actions"
                align="right"
                body={actionTemplate}
            />
            <Column
              field="statusId"
              header={buttonHeader}
              body={(rowData) =>
                rowData.children
                  ? actionFormatter(rowData.data)
                  : editButtonFormatter(rowData.data)
              }
            />
             
          </TreeTable>
        </div>
      ) : (
        <Alert color="info">No data to display</Alert>
      )}
    </>
  );
};

export default BuildingsTable;